import { Component } from '@angular/core';
import { AbstractSettingsTabComponent } from '../abstract-settings-tab.component';
import { AlertService, Constants, InfoToggleComponent } from 'common';
import { CommonModule } from '@angular/common';
import { BatchTradeService } from '../../../../service';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { SettingsRepository } from '../../../../repository';

@Component({
  selector: 'common-rebalancing-general-settings',
  standalone: true,
  imports: [CommonModule, TranslateModule, InfoToggleComponent, FormsModule],
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss'],
})
export class GeneralSettingsComponent extends AbstractSettingsTabComponent {
  public automatedMarketMakerEnabled: boolean;
  public rfqtEnabled: boolean;
  public batchTradeSupported: boolean;
  public batchTrade: boolean;
  public revertOnError: boolean;
  public maxSlippage: number;
  public maxSlippageInvalid = false;
  public maxPriceImpact: number;
  public maxPriceImpactInvalid = false;
  public minExpirySec: number;
  public minExpirySecInvalid = false;
  public minTradeValue: number;
  public minTradeValueInvalid = false;

  constructor(
    private settingsRepository: SettingsRepository,
    private batchTradeService: BatchTradeService,
    protected override alertService: AlertService,
  ) {
    super(alertService);
  }

  public get maxSlippageInPercent(): number {
    return +(this.maxSlippage * 100).toFixed(
      Constants.PERCENTAGE_FIXED_FRACTION_DIGITS,
    );
  }

  public set maxSlippageInPercent(proportionInPercent: number) {
    this.maxSlippage = +(proportionInPercent / 100).toFixed(
      Constants.PERCENTAGE_FIXED_FRACTION_DIGITS,
    );
  }

  public get maxPriceImpactInPercent(): number {
    return +(this.maxPriceImpact * 100).toFixed(
      Constants.PERCENTAGE_FIXED_FRACTION_DIGITS,
    );
  }

  public set maxPriceImpactInPercent(proportionInPercent: number) {
    this.maxPriceImpact = +(proportionInPercent / 100).toFixed(
      Constants.PERCENTAGE_FIXED_FRACTION_DIGITS,
    );
  }

  public override updateIsValid(): void {
    this.maxSlippageInvalid = !this.isSlippageValid();
    this.maxPriceImpactInvalid = !this.isMaxPriceImpactValid();
    this.minExpirySecInvalid = !this.isMinExpirySecValid();
    this.minTradeValueInvalid = !this.isMinTradeValueValid();
    super.updateIsValid();
  }

  private isSlippageValid(): boolean {
    return this.maxSlippage >= 0 && this.maxSlippage <= 1;
  }

  private isMaxPriceImpactValid(): boolean {
    return this.maxPriceImpact >= 0 && this.maxPriceImpact <= 1;
  }

  private isMinExpirySecValid(): boolean {
    return this.minExpirySec >= 0 && this.minExpirySec <= 120;
  }

  private isMinTradeValueValid(): boolean {
    return this.minTradeValue >= 0;
  }

  public init(): void {
    this.automatedMarketMakerEnabled =
      this.settingsRepository.getActiveChainSetting()
        ?.automatedMarketMakerEnabled === true;
    this.rfqtEnabled =
      this.settingsRepository.getActiveChainSetting()?.rfqtEnabled === true;
    this.batchTradeSupported = this.batchTradeService.isBatchTradeSupported();
    this.batchTrade =
      this.settingsRepository.getActiveChainSetting()?.batchTrade === true;
    this.revertOnError =
      this.settingsRepository.getActiveChainSetting()?.revertOnError === true;
    this.maxSlippage =
      this.settingsRepository.getActiveChainSetting()?.maxSlippage || 0.01;
    this.maxPriceImpact =
      this.settingsRepository.getActiveChainSetting()?.maxPriceImpact || 0.05;
    this.minExpirySec =
      this.settingsRepository.getActiveChainSetting()?.minExpirySec || 35;
    this.minTradeValue =
      this.settingsRepository.getActiveChainSetting()?.minTradeValue || 1;
  }

  protected isValid(): boolean {
    return (
      this.isSlippageValid() &&
      this.isMaxPriceImpactValid() &&
      this.isMinExpirySecValid() &&
      this.isMinTradeValueValid() &&
      (this.automatedMarketMakerEnabled || this.rfqtEnabled)
    );
  }

  public getValidationErrors(): string[] {
    const errors = [];
    if (!this.isSlippageValid()) {
      errors.push('settings.general.slippage.invalid');
    }
    if (!this.isMaxPriceImpactValid()) {
      errors.push('settings.general.priceImpact.invalid');
    }
    if (!this.isMinExpirySecValid()) {
      errors.push('settings.general.quoteExpiry.invalid');
    }
    if (!this.isMinTradeValueValid()) {
      errors.push('settings.general.tradeValue.invalid');
    }
    if (!(this.automatedMarketMakerEnabled || this.rfqtEnabled)) {
      errors.push('settings.general.switches.invalid');
    }
    return errors;
  }

  protected save(): void {
    if (this.isValid()) {
      this.settingsRepository.setBatchTradeSettings(
        this.batchTrade,
        this.revertOnError,
      );
      this.settingsRepository.setTradeSwitchSettings(
        this.automatedMarketMakerEnabled,
        this.rfqtEnabled,
      );
      this.settingsRepository.setMaxSlippage(this.maxSlippage);
      this.settingsRepository.setMaxPriceImpact(this.maxPriceImpact);
      this.settingsRepository.setMinExpirySec(this.minExpirySec);
      this.settingsRepository.setMinTradeValue(this.minTradeValue);
    }
  }
}
